import React from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { Provider } from 'react-redux'
import { StyleSheetManager } from 'styled-components'
import { OxygenProvider } from '@8x8/oxygen-config'
import URLS from '~/core/api-urls'
import store from '~/core/store'
import theme from '~/core/theme'
import api from '~/services/api'
import embeddedJavascript from './embedded'
import {
  awaitMapValues,
  createTemplate,
  decorateImage,
  fetchImageAsDataURL,
  imageToParticipantEntry,
  uniqueEntriesByKey
} from './utils'
import { isIOSWebView, shareWebViewBlob } from '~/core/utils'

export const fetchSessionTranscript = async sessionId => {
  const result = await api.get(URLS.TRANSCRIPT.PHRASES(sessionId))
  if (api.isErrorResponse(result)) {
    return []
  }
  if (result.phrases) {
    result.phrases.sort((p1, p2) => {
      if (p1.timestamp < p2.timestamp) {
        return -1
      }
      if (p1.timestamp > p2.timestamp) {
        return 1
      }
      return 0
    })
  }

  return result
}

export const downloadAsHtml = async (children, filename) => {
  const fakeHead = document.createElement('head')
  const renderedTree = renderToStaticMarkup(
    <StyleSheetManager target={fakeHead}>
      <OxygenProvider theme={theme.light} themeName={theme.light._name}>
        <Provider store={store}>
          {children}
        </Provider>
      </OxygenProvider>
    </StyleSheetManager>
  )

  // shortcut, can be improved if needed
  const parser = new DOMParser()
  const xml = parser.parseFromString(renderedTree, 'text/html')
  const images = Array.from(xml.getElementsByTagName('img'))

  const participantEntries = images.map(imageToParticipantEntry)
  images.forEach(decorateImage)

  const participantMapPromise = uniqueEntriesByKey(participantEntries)
    .map(([participant, src]) => [participant, fetchImageAsDataURL(src)])
    .reduce((map, [k, v]) => map.set(k, v), new Map())

  // wait for requests to finish
  const participantMap = await awaitMapValues(participantMapPromise)

  const script = document.createElement('script')
  script.innerHTML = embeddedJavascript

  const templates = Array.from(participantMap.entries())
    .map(([id, dataURL]) => Object.assign(createTemplate(), {
      id, innerHTML: dataURL
    }));

  [...templates, script].forEach(ele => fakeHead.appendChild(ele))

  const fileContents = new Blob([
    '<html>',
    fakeHead.outerHTML,
    '<body>',
    xml.documentElement.innerHTML,
    '</body></html>'
  ], {
    type: 'text/html'
  })

  const downloadLink = Object.assign(document.createElement('a'), {
    download: filename,
    href: URL.createObjectURL(fileContents)
  })

  downloadLink.click()
}

export const downloadAsText = async (text, fileName, isMobileSummary) => {
  if (!text) {
    return
  }

  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })

  if (isMobileSummary && isIOSWebView()) {
    await shareWebViewBlob(blob, fileName)
    return
  }

  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // fix for certain browsers
  setTimeout(() => {
    URL.revokeObjectURL(url)
  }, 0)
}
